import { FileText, ImageIcon, Music2 } from 'lucide-react'
import { lazy, Suspense } from 'react'
import { useDocumentThumbnail } from '@/features/identities/hooks/useDocumentThumbnail'
import type { LinkedDocument } from '@/features/identities/types'
import { isAudioFileType } from '@/utils/audio-playback'
import { cn } from '@/utils/cn'

const PdfFirstPage = lazy(() => import('./PdfFirstPage'))

function thumbnailKind(doc: LinkedDocument): 'image' | 'pdf' | 'docx' | 'audio' | 'file' {
  const type = doc.fileType?.toLowerCase() ?? ''
  const extension = doc.filename.split('.').pop()?.toLowerCase()
  if (type.startsWith('image/') || ['jpg', 'jpeg', 'png', 'webp'].includes(extension ?? '')) {
    return 'image'
  }
  if (type === 'application/pdf' || extension === 'pdf') return 'pdf'
  if (
    type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    extension === 'docx'
  )
    return 'docx'
  if (type.startsWith('audio/') || isAudioFileType(extension)) return 'audio'
  return 'file'
}

/** Download previews only when a visual document scrolls into view. */
export function DocumentThumbnail({ caseId, doc }: { caseId: string; doc: LinkedDocument }) {
  const kind = thumbnailKind(doc)
  const { setElement, url, excerpt } = useDocumentThumbnail(caseId, doc.documentId, kind)

  const Icon = kind === 'audio' ? Music2 : kind === 'image' ? ImageIcon : FileText
  return (
    <div
      ref={setElement}
      aria-hidden="true"
      className={cn(
        'flex size-20 shrink-0 justify-center overflow-hidden rounded-lg border border-border bg-muted text-muted-foreground',
        kind === 'pdf' ? 'items-start' : 'items-center'
      )}
    >
      {url && kind === 'image' ? (
        <img src={url} alt="" className="size-full object-cover" />
      ) : kind === 'docx' && excerpt ? (
        <span className="h-full w-full overflow-hidden bg-card p-2 text-left text-[7px] leading-tight text-foreground">
          {excerpt}
        </span>
      ) : url && kind === 'pdf' ? (
        <Suspense fallback={<Icon className="size-6" />}>
          <PdfFirstPage url={url} />
        </Suspense>
      ) : (
        <Icon className="size-6" />
      )}
    </div>
  )
}
